import { Injectable, OnDestroy } from "@angular/core";
import { PackageService } from "../../common/services/api/resources/package/package.service";
import { AccountResourceService } from "../../common/services/api/resources/package/account-resource.service";
import { IncludedResource } from "./api/resources/package/account-resource";
import { combineLatest, lastValueFrom, Observable, of, Subscription } from "rxjs";
import { map, mergeMap, take } from "rxjs/operators";
import { UserPackagePlan } from "./api/resources/package/package";
import { UserService } from "./api/resources/user/user.service";

export interface DynamicPricingProperties {
  packagePlan: UserPackagePlan;
  featureLimit: number;
  featurePricing: string;
  numUsed: number;
}

@Injectable({
  providedIn: "root"
})
export class DynamicPricingService implements OnDestroy {
  /** This service is used to generate pricing copy specifically for features that have some fixed quantity included under some packages (not Unlimited)
    * To use, call getDynamicPricing with the feature name (keys of featureNameMap map below), and an empty state boolean (true for empty states of features i.e. on the landing page not in set-up/wizard)
    * and set the result to the component's pricing text.
    * Example:
    *      this.dynamicPricing.getDynamicPricing("AdvVoicemailbox", false).then(result => {
              this.pricingText = result;
              this.cdRef.markForCheck();
            });
    * Note that getDynamicPricing returns a Promise.
     */

  featureNameMap = new Map([
    ["AdvVoicemailbox", "voice mailboxes"],
    ["AdvAnnouncement", "announcements"],
    ["GroupAddress", "groups"],
    ["AdvAttendantMenu", "attendant menus"],
    ["DbnDirectory", "dial-by-name directories"],
    ["AdvMohChannel", "music on hold channels"],
    ["RecordingRule", "unique users"],
    ["AdvParkChannel", "parking lots"],
    ["AdvDbnDirectory", "directories"]
  ]);

  private unsubscriber = new Subscription();

  constructor(
    private packageService: PackageService,
    private resourceService: AccountResourceService,
    private userService: UserService
  ) {}

  ngOnDestroy() {
    this.unsubscriber.unsubscribe();
  }

  async getDynamicPricing(featureName: string, emptyState: boolean = false): Promise<string> {
    return await this.getPackageInfo(featureName).then(
      (properties: DynamicPricingProperties | undefined) => {
        if (properties) {
          return this.getPricingText(featureName, properties, emptyState);
        } else {
          return "";
        }
      }
    );
  }

  getDynamicPricing$(featureName: string, emptyState: boolean = false): Observable<string> {
    return this.getPackageInfo$(featureName).pipe(
      map(properties => {
        if (properties) {
          return this.getPricingText(featureName, properties, emptyState);
        } else {
          return "";
        }
      })
    );
  }

  getPackageInfo$(featureName: string): Observable<DynamicPricingProperties | undefined> {
    return this.userService.selfUserIsAccountAdmin$.pipe(
      take(1),
      mergeMap(isAccountAdmin => {
        if (isAccountAdmin) {
          return combineLatest([
            this.packageService.getAssignedPrimaryPackages(),
            this.resourceService.accountResourceBrowse()
          ]).pipe(
            take(1),
            map(([packagesArray, resourcesResponse]) => {
              const properties: DynamicPricingProperties = {
                packagePlan: UserPackagePlan.BASIC,
                featureLimit: 0,
                featurePricing: "",
                numUsed: 0
              };

              if (resourcesResponse.status === "error") {
                return properties;
              }

              const resource = Object.values(resourcesResponse.data).find(
                res => res.name === featureName
              );
              properties.numUsed = resource?.used || 0;
              properties.featurePricing = resource?.monthlyRecurringCharge || "undetermined";

              if (packagesArray.length === 0) {
                properties.packagePlan = UserPackagePlan.FREE;
              }
              packagesArray.map(pkg => {
                if (pkg.status === "user") {
                  properties.packagePlan = UserPackagePlan.UNLIMITED;
                }
                properties.featureLimit +=
                  pkg.includedResources.find(
                    (res: IncludedResource) => res.resource.name === featureName
                  )?.quantity || 0;
              });

              return properties;
            })
          );
        } else {
          return of(undefined);
        }
      })
    );
  }

  private getPackageInfo(featureName: string): Promise<DynamicPricingProperties | undefined> {
    return lastValueFrom(this.getPackageInfo$(featureName));
  }

  private getPricingText(
    featureName: string,
    properties: DynamicPricingProperties,
    emptyState: boolean = false
  ): string {
    if (properties.packagePlan === UserPackagePlan.UNLIMITED) {
      return `You have unlimited ${this.featureNameMap.get(
        featureName
      )} included in your current plan.`;
    }
    if (featureName === "AdvParkChannel") {
      return `Your plan does not include ${this.featureNameMap.get(
        featureName
      )}. They may be purchased for $${properties.featurePricing}/month each.`;
    }
    if (properties.packagePlan === UserPackagePlan.FREE) {
      return `You have no ${this.featureNameMap.get(
        featureName
      )} included in your current plan. Additional ${this.featureNameMap.get(
        featureName
      )} will be billed at $${properties.featurePricing}  per month.`;
    }
    if (emptyState && properties.numUsed === 0) {
      return `You have ${properties.featureLimit} ${this.featureNameMap.get(
        featureName
      )} included in your plan. Additional ${this.featureNameMap.get(
        featureName
      )} will be billed at $${properties.featurePricing} per month.`;
    }
    if (properties.numUsed < properties.featureLimit) {
      return `You are currently using ${properties.numUsed} of ${
        properties.featureLimit
      } ${this.featureNameMap.get(
        featureName
      )} included in your plan. Additional ${this.featureNameMap.get(
        featureName
      )} will be billed at $${properties.featurePricing} per month.`;
    } else {
      return `You have used all ${this.featureNameMap.get(
        featureName
      )} included in your current plan. Additional ${this.featureNameMap.get(
        featureName
      )} will be billed at $${properties.featurePricing} per month.`;
    }
  }
}
